import type { ReactElement } from "react";

/**
 * Logo size options
 */
type LogoSize = "sm" | "md" | "lg" | "xl" | number;

/**
 * Logo component props
 */
interface LogoProps {
  size?: LogoSize;
  showText?: boolean;
  animate?: boolean;
  className?: string;
}

const SIZE_MAP: Record<string, number> = {
  sm: 56,
  md: 80,
  lg: 112,
  xl: 160,
};

/**
 * Tellor Logo Component
 * Uses the custom logo image from /images/logo/logo-fav-icon.png
 */
const Logo = ({
  size = "md",
  showText = false,
  animate = false,
  className = "",
}: LogoProps): ReactElement => {
  const pixelSize = typeof size === "number" ? size : SIZE_MAP[size] || 48;

  // Text size follows design system - not scaled to icon
  const getTextClass = (): string => {
    if (pixelSize <= 48) return "text-sm";
    if (pixelSize <= 80) return "text-base";
    return "text-xl";
  };

  return (
    <div className={`inline-flex flex-col items-center ${className}`}>
      {/* Light mode logo */}
      <img
        src="/images/logo/logo-fav-icon.png"
        alt="Tellor"
        className={`block dark:hidden ${animate ? "animate-logo-float" : ""}`}
        style={{
          height: pixelSize,
          width: "auto",
        }}
      />
      {/* Dark mode logo - brightened variant */}
      <img
        src="/images/logo/logo-fav-icon-dark.png"
        alt="Tellor"
        className={`hidden dark:block drop-shadow-[0_0_10px_rgba(255,200,160,0.3)] ${animate ? "animate-logo-float" : ""}`}
        style={{
          height: pixelSize,
          width: "auto",
        }}
      />

      {showText && (
        <span
          className={`mt-2 font-bold ${getTextClass()}`}
          style={{
            fontFamily: "Chewy, cursive",
            color: "var(--st-text-heading)",
            fontSize: pixelSize <= 48 ? "1rem" : pixelSize <= 80 ? "1.25rem" : "1.5rem",
          }}
        >
          Tellor
        </span>
      )}
    </div>
  );
};

export default Logo;
